import { TaskType } from 'api-validations/ag-concepts'
import { DataSource } from 'api-validations/shared'
import { type AvailableApplication, defaultLayer } from 'api-validations/stitch'

import type { NonEmptyArray } from './array'

/**
 * Ranks each value by its position in `order`, best first. Values grouped in a nested array tie.
 */
const rankFromOrder = <const T extends string>(order: readonly (T | readonly T[])[]) =>
  Object.fromEntries(
    order.flatMap((entry, rank) => {
      const normalizedToTies = typeof entry === 'string' ? [entry] : entry
      return normalizedToTies.map((value) => [value, rank] as const)
    })
  ) as Record<T, number> // Object.fromEntries types its keys as string; every key is a member of `order`.

const SOURCE_RANK: Record<DataSource, number> = rankFromOrder([
  DataSource.Gen3,
  DataSource.Gen2,
  [DataSource.ClimateDat, DataSource.JDShapefile, DataSource.ISOXML, DataSource.Unknown]
])

/**
 * Planting coverage is the cleanest outline of a field, and harvest the messiest, because growers
 * often run combines on lower-grade GPS than their RTK planters, sprayers, and strip-till rigs.
 */
const TASK_RANK: Record<TaskType, number> = rankFromOrder([
  TaskType.Plant,
  TaskType.Spray,
  TaskType.Sidedress,
  TaskType.Striptill,
  TaskType.Harvest
])

type CandidateStream = {
  dataSource: DataSource
  season: number
  task: TaskType
}

/**
 * Source outranks task, and task outranks season, because an outline from the wrong kind of pass
 * is worse than a stale one.
 */
const compareCandidates = (a: CandidateStream, b: CandidateStream) =>
  SOURCE_RANK[a.dataSource] - SOURCE_RANK[b.dataSource] ||
  TASK_RANK[a.task] - TASK_RANK[b.task] ||
  b.season - a.season

/**
 * Passes that together outline a field: every pass that ties on source, task, and season, so that
 * a replant or a second planter fills in ground the first pass missed.
 */
export type BoundarySource<T extends CandidateStream> = {
  season: number
  task: TaskType
  streams: NonEmptyArray<T>
}

/**
 * Groups a field's passes into the sources its boundary could be autogenerated from, best first.
 */
export const rankBoundarySources = <T extends CandidateStream>(
  fieldStreams: T[]
): BoundarySource<T>[] => {
  const sources: BoundarySource<T>[] = []
  for (const stream of fieldStreams.toSorted(compareCandidates)) {
    const previous = sources.at(-1)
    if (previous !== undefined && compareCandidates(previous.streams[0], stream) === 0) {
      previous.streams.push(stream)
    } else {
      sources.push({ season: stream.season, task: stream.task, streams: [stream] })
    }
  }
  return sources
}

const COVERAGE_LAYER_KEY = 'coverage'

/**
 * Picks the layer to contour for one of the passes in a {@link BoundarySource}.
 * Coverage traces exactly the ground a pass worked; a stream without it falls back to its
 * default layer.
 */
export const selectBoundaryLayer = (applications: AvailableApplication[]) => {
  for (const application of applications) {
    const coverage = application.layers.find((layer) => layer.key === COVERAGE_LAYER_KEY)
    if (coverage !== undefined) {
      return { applicationId: application.application_id, layerId: coverage.id }
    }
  }
  return defaultLayer(applications)
}
